import React from "react";

/**
 * Shared game iconography — drawn SVGs (not emoji) so the athlete UI
 * reads as *designed*. Points use a star, streak a flame, lives a
 * heart, etc. One component, `name` picks the glyph.
 */
export type GameIconName =
  | "star" | "flame" | "heart" | "bulb" | "bolt"
  | "medal" | "trophy" | "check" | "x" | "lock" | "clock";

const PATHS: Record<GameIconName, React.ReactNode> = {
  star: (
    <path d="M12 2.5l2.9 5.9 6.5.95-4.7 4.6 1.1 6.45L12 17.9l-5.8 3 1.1-6.45-4.7-4.6 6.5-.95L12 2.5Z" fill="#f59e0b" stroke="#d97706" strokeWidth="1" strokeLinejoin="round" />
  ),
  flame: (
    <>
      <path d="M12 2c1 3-1.5 4.2-1.5 6.5 0 1.2.8 2 .8 2S9 9.8 8 8.5C6 10 5 12 5 14.5 5 18.6 8.1 21 12 21s7-2.4 7-6.5c0-3.8-2.6-5.7-3.8-8C14.2 4.3 13 3 12 2Z" fill="#fb923c" />
      <path d="M12 21c2.2 0 3.8-1.5 3.8-3.6 0-2-1.4-3-2.3-4.4-.7 1.4-2 1.7-2 3.2 0 .8.5 1.3.5 1.3s-1.2-.3-1.6-1.2c-.7.8-1.1 1.6-1.1 2.6C9.3 20 10.6 21 12 21Z" fill="#fcd34d" />
    </>
  ),
  heart: (
    <path d="M12 20.5S3.5 15 3.5 8.9C3.5 6 5.8 4 8.4 4c1.7 0 3 .9 3.6 2 .6-1.1 1.9-2 3.6-2 2.6 0 4.9 2 4.9 4.9 0 6.1-8.5 11.6-8.5 11.6Z" fill="#ef4444" />
  ),
  bulb: (
    <>
      <path d="M9 18h6M10 21h4" stroke="#b45309" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 3a6 6 0 00-3.6 10.8c.6.5 1.1 1.2 1.1 2H14.5c0-.8.5-1.5 1.1-2A6 6 0 0012 3Z" fill="#fde68a" stroke="#b45309" strokeWidth="1.4" />
    </>
  ),
  bolt: (
    <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8Z" fill="#f59e0b" stroke="#d97706" strokeWidth="1" strokeLinejoin="round" />
  ),
  medal: (
    <>
      <path d="M8 3l2.5 5M16 3l-2.5 5" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="14" r="6" fill="#fbbf24" stroke="#d97706" strokeWidth="1.4" />
      <circle cx="12" cy="14" r="3" fill="#fde68a" />
    </>
  ),
  trophy: (
    <>
      <path d="M7 4h10v4a5 5 0 01-10 0V4Z" fill="#fbbf24" stroke="#d97706" strokeWidth="1.3" />
      <path d="M7 6H4.5a2.5 2.5 0 002.5 2.5M17 6h2.5a2.5 2.5 0 01-2.5 2.5" stroke="#d97706" strokeWidth="1.3" fill="none" />
      <path d="M10 13v3h4v-3M8 20h8M12 16v4" stroke="#d97706" strokeWidth="1.6" strokeLinecap="round" />
    </>
  ),
  check: (
    <path d="M5 12.5l4.2 4.2L19 7" stroke="currentColor" strokeWidth="3.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  ),
  x: (
    <path d="M7 7l10 10M17 7L7 17" stroke="currentColor" strokeWidth="3.2" fill="none" strokeLinecap="round" />
  ),
  lock: (
    <path fillRule="evenodd" clipRule="evenodd" d="M6 10V8a6 6 0 1112 0v2a2.5 2.5 0 012.5 2.5v6A2.5 2.5 0 0118 21H6a2.5 2.5 0 01-2.5-2.5v-6A2.5 2.5 0 016 10Zm10-2v2H8V8a4 4 0 118 0Z" fill="currentColor" />
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" fill="#fff" stroke="#c9d4e1" strokeWidth="2" />
      <path d="M12 7v5l3 2" stroke="#17223b" strokeWidth="2" fill="none" strokeLinecap="round" />
    </>
  ),
};

export default function GameIcon({
  name,
  className = "w-5 h-5",
}: {
  name: GameIconName;
  className?: string;
}) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      {PATHS[name]}
    </svg>
  );
}
